import { useState } from "react";
import { toast } from "react-toastify";
import useFormValidation from "./formValidation";
import { useAuth } from "../services/firebase/auth";
import { getQueues, Queue } from "../model/queue.model";
import {
  getWhatsappBotConfig,
  saveWhatsappBotConfig,
  WhatsappBotMenuNode,
} from "../model/whatsappBot.model";
import { emptyMenuNode } from "../model/whatsappBotMenu.utils";
import { Setter } from "../utils/types";

export default function useWhatsappBotPageHook(): WhatsappBotHook {
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [queues, setQueues] = useState<Queue[]>([]);

  const [enabled, setEnabled] = useState(false);
  const [fallbackQueueId, setFallbackQueueId] = useState("");
  const [menu, setMenu] = useState<WhatsappBotMenuNode>(emptyMenuNode());

  // Vale independente de `enabled` (bot de menu) — "horário de atendimento"
  // governa o canal WhatsApp como um todo, não só o menu.
  const [afterHoursEnabled, setAfterHoursEnabled] = useState(false);
  const [afterHoursMessage, setAfterHoursMessage] = useState("");
  const [afterHoursRouteToQueue, setAfterHoursRouteToQueue] = useState(false);

  // Só afeta conversa ainda presa no bot (nunca chegou a escolher uma
  // opção válida) — ver comentário equivalente no backend
  // (WhatsappBotConfig.expiration).
  const [expirationEnabled, setExpirationEnabled] = useState(false);
  const [expirationHours, setExpirationHours] = useState(48);
  const [expirationMessage, setExpirationMessage] = useState("");
  const [expirationRouteToQueue, setExpirationRouteToQueue] = useState(false);

  const auth = useAuth();
  const formValidation = useFormValidation();

  const load = async () => {
    setIsLoading(true);
    try {
      const loadedQueues = await getQueues(auth);
      setQueues(loadedQueues);

      const result = await getWhatsappBotConfig();
      if (!result.error && result.data) {
        setEnabled(result.data.enabled);
        setFallbackQueueId(result.data.fallbackQueueId);
        setMenu(result.data.root);
        if (result.data.afterHours) {
          setAfterHoursEnabled(result.data.afterHours.enabled);
          setAfterHoursMessage(result.data.afterHours.awayMessage);
          setAfterHoursRouteToQueue(result.data.afterHours.routeToQueue);
        }
        if (result.data.expiration) {
          setExpirationEnabled(result.data.expiration.enabled);
          setExpirationHours(result.data.expiration.hours);
          setExpirationMessage(result.data.expiration.message);
          setExpirationRouteToQueue(result.data.expiration.routeToQueue);
        }
      }
    } catch (e) {
      console.error(e);
      toast("Erro ao carregar a configuração do bot. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async () => {
    if (!formValidation.validateForm()) return;
    if (enabled && !fallbackQueueId) {
      toast("Selecione uma fila padrão antes de ativar o bot.");
      return;
    }
    if (afterHoursEnabled && !afterHoursMessage.trim()) {
      toast("Preencha a mensagem automática antes de ativar o aviso de fora do horário.");
      return;
    }
    if (expirationEnabled && !expirationMessage.trim()) {
      toast("Preencha a mensagem de encerramento antes de ativar a expiração.");
      return;
    }
    if (expirationEnabled && expirationHours <= 0) {
      toast("Informe um tempo de expiração maior que zero.");
      return;
    }
    setIsLoading(true);
    try {
      const result = await saveWhatsappBotConfig({
        enabled,
        fallbackQueueId,
        root: menu,
        // Sempre reenviado, mesmo desligado — saveWhatsappBotConfig
        // SOBRESCREVE a config inteira no backend (não faz merge), então
        // omitir isso aqui apagaria o que já estava salvo em qualquer save
        // (inclusive um save disparado só por editar a árvore do menu).
        afterHours: {
          enabled: afterHoursEnabled,
          awayMessage: afterHoursMessage,
          routeToQueue: afterHoursRouteToQueue,
        },
        expiration: {
          enabled: expirationEnabled,
          hours: expirationHours,
          message: expirationMessage,
          routeToQueue: expirationRouteToQueue,
        },
      });
      if (result.error) throw new Error();
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error(e);
      toast("Erro ao salvar. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formValidation,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isLoading,
    setIsLoading,
    load,
    onSave,
    queues,
    enabled,
    setEnabled,
    fallbackQueueId,
    setFallbackQueueId,
    menu,
    setMenu,
    afterHoursEnabled,
    setAfterHoursEnabled,
    afterHoursMessage,
    setAfterHoursMessage,
    afterHoursRouteToQueue,
    setAfterHoursRouteToQueue,
    expirationEnabled,
    setExpirationEnabled,
    expirationHours,
    setExpirationHours,
    expirationMessage,
    setExpirationMessage,
    expirationRouteToQueue,
    setExpirationRouteToQueue,
  };
}

export type WhatsappBotHook = {
  formValidation: any;
  load: () => Promise<void>;
  onSave: () => Promise<void>;

  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Setter<boolean>;

  isLoading: boolean;
  setIsLoading: Setter<boolean>;

  queues: Queue[];

  enabled: boolean;
  setEnabled: Setter<boolean>;

  fallbackQueueId: string;
  setFallbackQueueId: Setter<string>;

  menu: WhatsappBotMenuNode;
  setMenu: Setter<WhatsappBotMenuNode>;

  afterHoursEnabled: boolean;
  setAfterHoursEnabled: Setter<boolean>;

  afterHoursMessage: string;
  setAfterHoursMessage: Setter<string>;

  afterHoursRouteToQueue: boolean;
  setAfterHoursRouteToQueue: Setter<boolean>;

  expirationEnabled: boolean;
  setExpirationEnabled: Setter<boolean>;

  expirationHours: number;
  setExpirationHours: Setter<number>;

  expirationMessage: string;
  setExpirationMessage: Setter<string>;

  expirationRouteToQueue: boolean;
  setExpirationRouteToQueue: Setter<boolean>;
};
